import React from 'react';
import Popover from 'react-bootstrap/Popover';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import moment from 'moment';
import { formatDecimalTime } from '../../../util/common';
import { toDecimal, roundValue } from '../../../util/decimal-utils';

/**
 * Formats internal work hour time period enum types into clean human-readable labels for modals.
 *
 * @param {string} timeType - Work hour time period type (e.g., 'STANDARD', 'OVERTIME_1', 'PUBLIC_HOLIDAY').
 * @returns {string} User-facing label (e.g., 'Standard', 'OVERTIME', 'HOLIDAY', 'Unpaid Allowance').
 */
export const formatTimeTypeLabel = (timeType) => {
    if (!timeType) return '';
    if (timeType === 'STANDARD') return 'Standard';
    if (timeType === 'OVERTIME_1' || timeType === 'OVERTIME') return 'OVERTIME';
    if (timeType === 'PUBLIC_HOLIDAY' || timeType === 'HOLIDAY') return 'HOLIDAY';
    if (timeType === 'UNPAID_ALLOWANCE') return 'Unpaid Allowance';
    return timeType.replace(/_/g, ' ');
};

/**
 * Formats internal work hour time period enum types into compact table abbreviations.
 *
 * @param {string} timeType - Work hour time period type.
 * @returns {string} Short abbreviation ('Std', 'Hol', 'Unpaid', 'OT').
 */
export const formatPeriodShortLabel = (timeType) => {
    if (timeType === 'STANDARD') return 'Std';
    if (timeType === 'PUBLIC_HOLIDAY' || timeType === 'HOLIDAY' || timeType?.includes('HOLIDAY')) return 'Hol';
    if (timeType === 'UNPAID_ALLOWANCE') return 'Unpaid';
    return 'OT';
};

/**
 * Renders the table column cell for work hour breakdown, including time periods and unclassified warning overlay.
 *
 * @param {Object} rowData - Allocation row data with work_hour_breakdown and entryHours.
 * @returns {React.ReactNode} Formatted breakdown cell content.
 */
export const renderBreakdownCell = (rowData) => {
    const b = rowData?.work_hour_breakdown;
    if (!b?.time_periods?.length) return '—';

    const periodLabels = b.time_periods.map(
        (p) => `${formatDecimalTime(p.hours)} ${formatPeriodShortLabel(p.time_type)}`
    );
    const classifiedHours = b.time_periods.reduce((sum, p) => sum + (parseFloat(p.hours) || 0), 0);
    const unclassifiedHours = (rowData?.entryHours || 0) - classifiedHours;
    const hasUnclassified = unclassifiedHours > 0.05;

    if (!hasUnclassified) return periodLabels.join(' | ');

    return (
        <OverlayTrigger
            placement="top"
            overlay={
                <Popover>
                    <Popover.Body style={{ fontSize: '0.8rem' }}>
                        Some worked hours fall outside the configured work hour periods and are not included in the cost breakdown. Review the Work Hour configuration to ensure full-day coverage.
                    </Popover.Body>
                </Popover>
            }
        >
            <span>
                {periodLabels.join(' | ')}
                {' | '}
                <span style={{ color: '#f59e0b', cursor: 'help' }}>
                    ⚠ {formatDecimalTime(unclassifiedHours)} Unclassified
                </span>
            </span>
        </OverlayTrigger>
    );
};

/**
 * Classifies a single timesheet allocation's hours and costs into Standard, Overtime, and Holiday buckets
 * using Decimal.js precision.
 *
 * @param {Object} allocation - Enriched allocation row object.
 * @returns {{
 *   standardHours: import('decimal.js').Decimal,
 *   standardCost: import('decimal.js').Decimal,
 *   overtimeHours: import('decimal.js').Decimal,
 *   overtimeCost: import('decimal.js').Decimal,
 *   holidayHours: import('decimal.js').Decimal,
 *   holidayCost: import('decimal.js').Decimal,
 *   totalHours: import('decimal.js').Decimal,
 *   totalCost: import('decimal.js').Decimal
 * }} Decimal breakdown values for the allocation.
 */
export const classifyEntryBreakdown = (allocation) => {
    let standardHours = toDecimal(0);
    let standardCost = toDecimal(0);
    let overtimeHours = toDecimal(0);
    let overtimeCost = toDecimal(0);
    let holidayHours = toDecimal(0);
    let holidayCost = toDecimal(0);

    const timePeriods = allocation?.work_hour_breakdown?.time_periods;

    if (Array.isArray(timePeriods) && timePeriods.length > 0) {
        timePeriods.forEach((period) => {
            const timeType = String(period?.time_type || '').toUpperCase();
            if (timeType === 'UNPAID_ALLOWANCE') {
                return;
            }

            const hours = toDecimal(period?.hours);
            const cost = toDecimal(period?.cost);

            if (timeType === 'STANDARD') {
                standardHours = standardHours.plus(hours);
                standardCost = standardCost.plus(cost);
            } else if (timeType === 'PUBLIC_HOLIDAY' || timeType === 'HOLIDAY' || timeType.includes('HOLIDAY')) {
                holidayHours = holidayHours.plus(hours);
                holidayCost = holidayCost.plus(cost);
            } else {
                overtimeHours = overtimeHours.plus(hours);
                overtimeCost = overtimeCost.plus(cost);
            }
        });
    } else {
        const startMoment = moment.utc(allocation?.approvedStartTime || allocation?.startTime);
        const endMoment = moment.utc(allocation?.approvedEndTime || allocation?.endTime);
        let durationHours = toDecimal(0);

        if (startMoment.isValid() && endMoment.isValid()) {
            const diffMinutes = endMoment.diff(startMoment, 'minutes');
            if (diffMinutes > 0) {
                durationHours = toDecimal(diffMinutes).dividedBy(toDecimal(60));
            }
        }

        const ot1 = toDecimal(allocation?.overtime_1);
        const ot2 = toDecimal(allocation?.overtime_2);
        overtimeHours = ot1.plus(ot2);

        if (durationHours.greaterThan(overtimeHours)) {
            standardHours = durationHours.minus(overtimeHours);
        } else {
            standardHours = toDecimal(0);
        }

        const rate = toDecimal(allocation?.hourlyRate);
        standardCost = standardHours.times(rate);
        overtimeCost = overtimeHours.times(rate);
    }

    const totalHours = standardHours.plus(overtimeHours).plus(holidayHours);
    const totalCost = standardCost.plus(overtimeCost).plus(holidayCost);

    return {
        standardHours,
        standardCost,
        overtimeHours,
        overtimeCost,
        holidayHours,
        holidayCost,
        totalHours,
        totalCost,
    };
};

/**
 * Computes summed totals across report rows using Decimal.js precision to guarantee cross-footing integrity.
 * Total Hours = Standard Hours + Overtime Hours + Holiday Hours
 * Total Cost = Standard Cost + Overtime Cost + Holiday Cost
 *
 * @param {Array<Object>} rows - Array of user summary rows
 * @returns {{
 *   totalWorkers: number,
 *   totalEntries: number,
 *   standardHours: number,
 *   standardCost: number,
 *   overtimeHours: number,
 *   overtimeCost: number,
 *   holidayHours: number,
 *   holidayCost: number,
 *   totalHours: number,
 *   totalCost: number
 * }} Reconciled report totals
 */
export const computeReportTotals = (rows = []) => {
    let totalEntries = toDecimal(0);
    let standardHours = toDecimal(0);
    let standardCost = toDecimal(0);
    let overtimeHours = toDecimal(0);
    let overtimeCost = toDecimal(0);
    let holidayHours = toDecimal(0);
    let holidayCost = toDecimal(0);

    (Array.isArray(rows) ? rows : []).forEach((row) => {
        totalEntries = totalEntries.plus(toDecimal(row?.totalEntries || 0));
        standardHours = standardHours.plus(toDecimal(row?.standardHours || 0));
        standardCost = standardCost.plus(toDecimal(row?.standardCost || 0));
        overtimeHours = overtimeHours.plus(toDecimal(row?.overtimeHours || 0));
        overtimeCost = overtimeCost.plus(toDecimal(row?.overtimeCost || 0));
        holidayHours = holidayHours.plus(toDecimal(row?.holidayHours || 0));
        holidayCost = holidayCost.plus(toDecimal(row?.holidayCost || 0));
    });

    const roundedStdHours = roundValue(standardHours, 2);
    const roundedStdCost = roundValue(standardCost, 2);
    const roundedOtHours = roundValue(overtimeHours, 2);
    const roundedOtCost = roundValue(overtimeCost, 2);
    const roundedHolHours = roundValue(holidayHours, 2);
    const roundedHolCost = roundValue(holidayCost, 2);

    const totalHours = roundValue(
        toDecimal(roundedStdHours).plus(toDecimal(roundedOtHours)).plus(toDecimal(roundedHolHours)),
        2
    );
    const totalCost = roundValue(
        toDecimal(roundedStdCost).plus(toDecimal(roundedOtCost)).plus(toDecimal(roundedHolCost)),
        2
    );

    return {
        totalWorkers: rows.length,
        totalEntries: totalEntries.toNumber(),
        standardHours: roundedStdHours,
        standardCost: roundedStdCost,
        overtimeHours: roundedOtHours,
        overtimeCost: roundedOtCost,
        holidayHours: roundedHolHours,
        holidayCost: roundedHolCost,
        totalHours,
        totalCost,
    };
};

/**
 * Aggregates filtered timesheet allocations into user-level summaries, project-wise breakdowns,
 * and grand total KPI metrics using Decimal.js.
 *
 * @param {Array<Object>} allocations - List of filtered allocation records.
 * @returns {{
 *   summary: Array<Object>,
 *   totals: Object,
 *   kpis: Object
 * }} Aggregated report summary and grand totals.
 */
export const aggregateTimesheetReport = (allocations = []) => {
    const userMap = new Map();

    if (Array.isArray(allocations)) {
        allocations.forEach((allocation) => {
            if (!allocation || allocation.status === 'LEAVE') return;

            const userKey = allocation.userId ? String(allocation.userId) : (allocation.empName || 'unknown');
            const empName = allocation.empName || 'Unknown User';
            const projectName = allocation.projectName || 'Unassigned Project';
            const projectKey = allocation.projectId ? String(allocation.projectId) : projectName;

            const classified = classifyEntryBreakdown(allocation);

            if (!userMap.has(userKey)) {
                userMap.set(userKey, {
                    userId: allocation.userId || userKey,
                    empName,
                    isGlobal: Boolean(allocation.isGlobal || allocation.is_global),
                    projectMap: new Map(),
                });
            } else {
                const existing = userMap.get(userKey);
                if (!existing.isGlobal && Boolean(allocation.isGlobal || allocation.is_global)) {
                    existing.isGlobal = true;
                }
            }

            const userGroup = userMap.get(userKey);

            if (!userGroup.projectMap.has(projectKey)) {
                userGroup.projectMap.set(projectKey, {
                    projectId: allocation.projectId || projectKey,
                    projectName,
                    entries: 0,
                    standardHours: toDecimal(0),
                    standardCost: toDecimal(0),
                    overtimeHours: toDecimal(0),
                    overtimeCost: toDecimal(0),
                    holidayHours: toDecimal(0),
                    holidayCost: toDecimal(0),
                });
            }

            const projGroup = userGroup.projectMap.get(projectKey);
            projGroup.entries += 1;
            projGroup.standardHours = projGroup.standardHours.plus(classified.standardHours);
            projGroup.standardCost = projGroup.standardCost.plus(classified.standardCost);
            projGroup.overtimeHours = projGroup.overtimeHours.plus(classified.overtimeHours);
            projGroup.overtimeCost = projGroup.overtimeCost.plus(classified.overtimeCost);
            projGroup.holidayHours = projGroup.holidayHours.plus(classified.holidayHours);
            projGroup.holidayCost = projGroup.holidayCost.plus(classified.holidayCost);
        });
    }

    const summary = [];
    userMap.forEach((userGroup) => {
        const projects = [];
        let userTotalEntries = toDecimal(0);
        let userStandardHours = toDecimal(0);
        let userStandardCost = toDecimal(0);
        let userOvertimeHours = toDecimal(0);
        let userOvertimeCost = toDecimal(0);
        let userHolidayHours = toDecimal(0);
        let userHolidayCost = toDecimal(0);

        userGroup.projectMap.forEach((proj) => {
            const standardHours = roundValue(proj.standardHours, 2);
            const standardCost = roundValue(proj.standardCost, 2);
            const overtimeHours = roundValue(proj.overtimeHours, 2);
            const overtimeCost = roundValue(proj.overtimeCost, 2);
            const holidayHours = roundValue(proj.holidayHours, 2);
            const holidayCost = roundValue(proj.holidayCost, 2);

            const totalHours = roundValue(
                toDecimal(standardHours).plus(toDecimal(overtimeHours)).plus(toDecimal(holidayHours)),
                2
            );
            const totalCost = roundValue(
                toDecimal(standardCost).plus(toDecimal(overtimeCost)).plus(toDecimal(holidayCost)),
                2
            );

            projects.push({
                projectId: proj.projectId,
                projectName: proj.projectName,
                totalEntries: proj.entries,
                standardHours,
                standardCost,
                overtimeHours,
                overtimeCost,
                holidayHours,
                holidayCost,
                totalHours,
                totalCost,
            });

            userTotalEntries = userTotalEntries.plus(toDecimal(proj.entries));
            userStandardHours = userStandardHours.plus(toDecimal(standardHours));
            userStandardCost = userStandardCost.plus(toDecimal(standardCost));
            userOvertimeHours = userOvertimeHours.plus(toDecimal(overtimeHours));
            userOvertimeCost = userOvertimeCost.plus(toDecimal(overtimeCost));
            userHolidayHours = userHolidayHours.plus(toDecimal(holidayHours));
            userHolidayCost = userHolidayCost.plus(toDecimal(holidayCost));
        });

        projects.sort((a, b) => (a.projectName || '').localeCompare(b.projectName || ''));

        const roundedUserStdHours = roundValue(userStandardHours, 2);
        const roundedUserStdCost = roundValue(userStandardCost, 2);
        const roundedUserOtHours = roundValue(userOvertimeHours, 2);
        const roundedUserOtCost = roundValue(userOvertimeCost, 2);
        const roundedUserHolHours = roundValue(userHolidayHours, 2);
        const roundedUserHolCost = roundValue(userHolidayCost, 2);

        const userTotalHours = roundValue(
            toDecimal(roundedUserStdHours).plus(toDecimal(roundedUserOtHours)).plus(toDecimal(roundedUserHolHours)),
            2
        );
        const userTotalCost = roundValue(
            toDecimal(roundedUserStdCost).plus(toDecimal(roundedUserOtCost)).plus(toDecimal(roundedUserHolCost)),
            2
        );

        summary.push({
            userId: userGroup.userId,
            empName: userGroup.empName,
            totalEntries: userTotalEntries.toNumber(),
            standardHours: roundedUserStdHours,
            standardCost: roundedUserStdCost,
            overtimeHours: roundedUserOtHours,
            overtimeCost: roundedUserOtCost,
            holidayHours: roundedUserHolHours,
            holidayCost: roundedUserHolCost,
            totalHours: userTotalHours,
            totalCost: userTotalCost,
            isGlobal: userGroup.isGlobal,
            projects,
        });
    });

    summary.sort((a, b) => {
        const nameComp = (a.empName || '').localeCompare(b.empName || '');
        if (nameComp !== 0) return nameComp;
        if (Boolean(a.isGlobal) !== Boolean(b.isGlobal)) return a.isGlobal ? 1 : -1;
        return String(a.userId || '').localeCompare(String(b.userId || ''));
    });

    const kpis = computeReportTotals(summary);

    return {
        summary,
        kpis,
        totals: kpis,
    };
};
